import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { loadTheme, saveTheme } from '../utils/storageUtils';

// Define theme context type
type ThemeContextType = {
  mode: 'light' | 'dark';
  toggleTheme: () => void;
};

// Create theme context
const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleTheme: () => {},
});

// Custom hook to use theme context
export const useTheme = () => useContext(ThemeContext);

// Theme provider props
interface ThemeProviderProps {
  children: ReactNode;
}

// Theme provider component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // State for theme mode
  const [mode, setMode] = useState<'light' | 'dark'>('light');
  
  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = loadTheme();
    if (savedTheme === 'dark' || savedTheme === 'light') {
      setMode(savedTheme);
    }
  }, []);
  
  // Toggle theme
  const toggleTheme = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    saveTheme(newMode);
  };
  
  // Create theme
  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: '#00BFFF', // Deep Sky Blue - more vibrant and modern
        light: '#33CCFF',
        dark: '#0099CC',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#FF00FF', // Magenta - bold and futuristic
        light: '#FF33FF',
        dark: '#CC00CC',
        contrastText: '#ffffff',
      },
      background: {
        default: mode === 'light' ? '#f0f2f5' : '#121212',
        paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
      },
      error: {
        main: '#FF3D71',
      },
      warning: {
        main: '#FFAA00',
      },
      info: {
        main: '#0095FF',
      },
      success: {
        main: '#00E096',
      },
    },
    typography: {
      fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '2.5rem',
        fontWeight: 700,
        letterSpacing: '-0.01562em',
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 700,
        letterSpacing: '-0.00833em',
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
        letterSpacing: '0em',
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        letterSpacing: '0.00735em',
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        letterSpacing: '0em',
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        letterSpacing: '0.0075em',
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: mode === 'light' 
              ? '0 8px 32px rgba(0, 0, 0, 0.08)' 
              : '0 8px 32px rgba(0, 0, 0, 0.4)',
            transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: mode === 'light' 
                ? '0 12px 40px rgba(0, 0, 0, 0.12)' 
                : '0 12px 40px rgba(0, 0, 0, 0.6)',
            },
            overflow: 'hidden',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 16px',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
          },
          contained: {
            background: 'linear-gradient(45deg, #00BFFF 30%, #33CCFF 90%)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
            '&.MuiChip-filled': {
              background: 'rgba(0, 191, 255, 0.1)',
              color: '#00BFFF',
              border: '1px solid rgba(0, 191, 255, 0.2)',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: mode === 'light' 
              ? 'linear-gradient(90deg, #00BFFF 0%, #0099CC 100%)' 
              : 'linear-gradient(90deg, #121212 0%, #1e1e1e 100%)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
    },
  });
  
  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
